import { useQuery } from "@tanstack/react-query";
import { projectService } from "projects/services";
import { IProject } from "projects/models";
import { PaginatedResult } from "shared/models";
import { useBoundStore } from "store";

type ProjectFilters = {
  organizationId?: string;
  projectId?: string;
  status?: string;
  subcontractor?: string;
  utilityProgram?: string;
  accountManager?: string;
  auditor?: string;
  customerPaymentMethod?: string;
  stage?: string;
  showArchived?: boolean;
  limit?: number;
  lastEvaluatedKey?: string;
};

export const projectKeys = {
  list: (tenantId: string | undefined, filters: ProjectFilters) => ["projects", tenantId, filters] as const,
  detail: (tenantId: string | undefined, organizationId: string | undefined, projectId?: string) => ["projects", tenantId, organizationId, "detail", projectId] as const,
};

export const useProjects = (filters: ProjectFilters, enabled = true) => {
  const { tenant, currentOrganization } = useBoundStore();

  return useQuery<PaginatedResult<IProject>>({
    queryKey: projectKeys.list(tenant?.id, filters),
    queryFn: async () => {
      const res = await projectService.getProjects(
        tenant!.id,
        filters.organizationId!,
        filters.projectId,
        filters.status,
        filters.subcontractor,
        filters.utilityProgram,
        filters.accountManager,
        filters.auditor,
        filters.customerPaymentMethod,
        filters.stage,
        filters.showArchived,
        filters.lastEvaluatedKey,
        filters.limit
      );
      return res;
    },
    enabled: enabled && Boolean(tenant?.id) && Boolean(filters.organizationId),
    staleTime: 5 * 60_000,
  });
};

export const useProject = (projectId?: string, enabled = true) => {
  const { tenant, currentOrganization } = useBoundStore();

  return useQuery<IProject>({
    queryKey: projectKeys.detail(tenant?.id, currentOrganization?.id, projectId),
    queryFn: async () => {
      const res = await projectService.getProject(tenant!.id, currentOrganization!.id, projectId as string);
      return res;
    },
    enabled: enabled && Boolean(tenant?.id) && Boolean(currentOrganization?.id) && Boolean(projectId),
    staleTime: 5 * 60_000,
  });
};
